import { useEffect, useRef, useState } from "react";
import { MessageCircle, ArrowRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { WA_LINK, SectionHeading } from "./shared";

export default function GallerySection({ city, lang, t }) {
  const [lightbox, setLightbox] = useState(-1);
  const [activeSlide, setActiveSlide] = useState(0);
  const scrollerRef = useRef(null);
  const touchStartX = useRef(null);
  const gallery = city.gallery || [];

  useEffect(() => {
    if (lightbox < 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setLightbox(-1);
        document.body.style.overflow = "";
      }
      if (e.key === "ArrowLeft") setLightbox(i => (i - 1 + gallery.length) % gallery.length);
      if (e.key === "ArrowRight") setLightbox(i => (i + 1) % gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, gallery.length]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const slide = Math.round(el.scrollLeft / (el.clientWidth * 0.5));
      setActiveSlide(Math.min(Math.max(slide, 0), gallery.length - 1));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [gallery.length]);

  if (gallery.length === 0) return null;

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-slide]");
    const step = card ? card.clientWidth + 16 : el.clientWidth * 0.6;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  const openLightbox = (i) => { setLightbox(i); document.body.style.overflow = "hidden"; };
  const close = () => { setLightbox(-1); document.body.style.overflow = ""; };
  const prev = (e) => { e?.stopPropagation(); setLightbox(i => (i - 1 + gallery.length) % gallery.length); };
  const next = (e) => { e?.stopPropagation(); setLightbox(i => (i + 1) % gallery.length); };
  const onTouchStart = (e) => { touchStartX.current = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) { if (dx < 0) next(); else prev(); }
    touchStartX.current = null;
  };

  return (
    <section id="gallery" className="bg-white py-16 sm:py-24 lg:py-32 overflow-hidden" data-testid="weddings-gallery">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
        <SectionHeading
          eyebrow={t.galleryEyebrow}
          title={t.galleryTitle}
          titleAccent={t.galleryTitleAccent}
          subtitle={t.gallerySubtitle}
        />
      </div>

      <div className="relative">
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          aria-label="Previous"
          className="hidden lg:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-lg border border-[#EFE4D2] items-center justify-center hover:bg-[#FAF5EE] hover:border-[#C9A87C] transition"
          data-testid="gallery-arrow-prev"
        >
          <ChevronLeft className="w-5 h-5 text-[#8B6F47]" />
        </button>
        <button
          type="button"
          onClick={() => scrollBy(1)}
          aria-label="Next"
          className="hidden lg:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-white shadow-lg border border-[#EFE4D2] items-center justify-center hover:bg-[#FAF5EE] hover:border-[#C9A87C] transition"
          data-testid="gallery-arrow-next"
        >
          <ChevronRight className="w-5 h-5 text-[#8B6F47]" />
        </button>

        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2 px-5 sm:px-10"
          style={{ scrollPaddingLeft: "20px", WebkitOverflowScrolling: "touch" }}
        >
          {gallery.map((img, i) => {
            const alt = (img.alt && (img.alt[lang] || img.alt.ru)) || "";
            const tag = (img.tag && (img.tag[lang] || img.tag.ru)) || "";
            return (
              <button
                key={i}
                type="button"
                data-slide
                onClick={() => openLightbox(i)}
                className="group relative shrink-0 snap-start overflow-hidden rounded-sm bg-[#1F1815] w-[78%] sm:w-[48%] lg:w-[32%] xl:w-[28%] aspect-[3/4] focus:outline-none focus:ring-2 focus:ring-[#C9A87C]"
                data-testid={`weddings-gallery-${i}`}
              >
                <img
                  src={img.src}
                  srcSet={`${img.src.replace(/\.webp$/, "-sm.webp")} 640w, ${img.src} 1080w`}
                  sizes="(max-width: 640px) 78vw, (max-width: 1024px) 48vw, (max-width: 1280px) 32vw, 28vw"
                  alt={alt}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1410]/85 via-[#1A1410]/20 to-transparent" />
                <div className="absolute left-5 right-5 bottom-5 text-left">
                  <p className="text-[9px] sm:text-[10px] tracking-[0.3em] text-[#D4B896] mb-2 font-light">
                    {String(i + 1).padStart(2, "0")} / {String(gallery.length).padStart(2, "0")}
                  </p>
                  <h3 className="text-white text-lg sm:text-xl lg:text-2xl leading-tight"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
                    {tag}
                  </h3>
                </div>
              </button>
            );
          })}
          <div className="shrink-0 w-2 sm:w-6" aria-hidden="true" />
        </div>

        <div className="flex justify-center items-center gap-1.5 mt-7 sm:mt-9" data-testid="gallery-dots">
          {gallery.map((_, i) => (
            <span
              key={i}
              className={`h-px transition-all duration-300 ${i === activeSlide ? "w-8 bg-[#8B6F47]" : "w-4 bg-[#EFE4D2]"}`}
            />
          ))}
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 sm:px-10">
        <div className="text-center mt-10 sm:mt-12">
          <a
            href={WA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 sm:gap-3 px-7 sm:px-9 py-3.5 sm:py-4 border border-[#C9A87C] text-[#8B6F47] hover:bg-[#C9A87C] hover:text-white text-[11px] sm:text-[12px] tracking-[0.2em] sm:tracking-[0.25em] font-medium transition rounded-sm"
            data-testid="weddings-gallery-cta"
          >
            <MessageCircle className="w-4 h-4" />{t.galleryCta} <ArrowRight className="w-3 h-3" />
          </a>
        </div>
      </div>

      {lightbox >= 0 && (
        <div
          className="fixed inset-0 z-50 bg-[#1A1410]/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={close}
          data-testid="weddings-gallery-lightbox"
        >
          <button onClick={close} aria-label="Close"
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                  data-testid="weddings-gallery-close">
            <X className="w-5 h-5" />
          </button>
          <button onClick={prev} aria-label="Previous"
                  className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                  data-testid="weddings-gallery-prev">
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <button onClick={next} aria-label="Next"
                  className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
                  data-testid="weddings-gallery-next">
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <div className="relative max-w-5xl w-full max-h-[88vh] flex flex-col items-center"
               onClick={(e) => e.stopPropagation()}
               onTouchStart={onTouchStart}
               onTouchEnd={onTouchEnd}>
            <img src={gallery[lightbox].src}
                 alt={(gallery[lightbox].alt && (gallery[lightbox].alt[lang] || gallery[lightbox].alt.ru)) || ""}
                 className="max-h-[78vh] w-auto max-w-full object-contain rounded-sm shadow-2xl" />
            <div className="mt-4 flex items-center justify-between w-full text-white/80 text-xs sm:text-sm">
              <span className="text-[10px] sm:text-[11px] tracking-[0.3em] text-[#D4B896]">
                {((gallery[lightbox].tag && (gallery[lightbox].tag[lang] || gallery[lightbox].tag.ru)) || "").toUpperCase()}
              </span>
              <span className="font-light">{lightbox + 1} / {gallery.length}</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
